<template>
  <PageShell title="Add Inventory" subtitle="How did you get these items?">
    <template #actions>
      <router-link class="ll-pill ghost" to="/dashboard">Dashboard</router-link>
      <router-link class="ll-pill" to="/wizard">Quick Add</router-link>
    </template>

    <!-- Mode tiles -->
    <section v-if="mode === ''" class="card-head">
      <div class="card-title">Choose an entry path</div>
      <div class="card-hint">
        Collectibles Edition is optimized for lots, comps, and clean profit tracking.
      </div>

      <div class="tiles">
        <button class="tile" type="button" @click="go('assistant')">
          <div class="tile-top">
            <div class="badge">Fast</div>
            <div class="tile-title">Tell Me What You Bought</div>
          </div>
          <div class="tile-sub">Describe it — I’ll draft everything</div>
        </button>

        <button class="tile" type="button" @click="go('manual')">
          <div class="tile-top">
            <div class="badge alt">Precise</div>
            <div class="tile-title">Quick Manual Entry</div>
          </div>
          <div class="tile-sub">Keyboard-first grid</div>
        </button>

        <button class="tile disabled" type="button" @click="go('import')">
          <div class="tile-top">
            <div class="badge soon">Soon</div>
            <div class="tile-title">Import from Marketplace</div>
          </div>
          <div class="tile-sub">eBay, and more coming soon</div>
        </button>

        <button class="tile disabled" type="button" @click="go('csv')">
          <div class="tile-top">
            <div class="badge soon">Soon</div>
            <div class="tile-title">Import CSV</div>
          </div>
          <div class="tile-sub">Bulk upload from a file</div>
        </button>
      </div>
    </section>

    <!-- Assistant mode -->
    <section v-if="mode === 'assistant'" class="mode-wrap">
      <div class="mode-topbar">
        <div class="mode-title">Assistant Draft</div>
        <button class="ll-pill ghost" type="button" @click="reset">Back</button>
      </div>
      <AssistantMode @exit="reset" @review="openReview" />
    </section>

    <!-- Manual entry -->
    <section v-if="mode === 'manual'" class="mode-wrap">
      <div class="mode-topbar">
        <div class="mode-title">Manual Entry</div>
        <button class="ll-pill ghost" type="button" @click="mode = ''">Back</button>
      </div>
      <ManualEntryGrid @cancel="mode = ''" @review="openReview" />
    </section>

    <!-- Review -->
    <section v-if="mode === 'review'" class="mode-wrap">
      <div class="mode-topbar">
        <div class="mode-title">Review & Confirm</div>
        <button class="ll-pill ghost" type="button" @click="reset">Cancel</button>
      </div>
      <DraftReview
        :draft="draft"
        @back="mode = 'manual'"
        @confirm="onConfirmed"
        @cancel="reset"
      />
    </section>

    <!-- Coming soon -->
    <section v-if="mode === 'import' || mode === 'csv'" class="mode-wrap">
      <div class="mode-topbar">
        <div class="mode-title">Coming Next</div>
        <button class="ll-pill ghost" type="button" @click="mode = ''">Close</button>
      </div>

      <div class="coming">
        <div class="coming-text">
          This path will land in the same Review screen. For now, start with
          <strong>Tell Me What You Bought</strong> or <strong>Quick Manual Entry</strong>.
        </div>

        <div class="cta-row">
          <button class="ll-pill" type="button" @click="go('assistant')">Use Assistant</button>
          <button class="ll-pill ghost" type="button" @click="go('manual')">Manual Entry</button>
        </div>
      </div>
    </section>
  </PageShell>
</template>

<script>
import PageShell from "@/components/layout/PageShell.vue";
import ManualEntryGrid from "@/components/addInventory/ManualEntryGrid.vue";
import DraftReview from "@/components/addInventory/DraftReview.vue";
import AssistantMode from "@/components/addInventory/AssistantMode.vue";

export default {
  name: "AddInventory",
  components: { PageShell, ManualEntryGrid, DraftReview, AssistantMode },

  data() {
    return {
      mode: "",
      draft: null,
    };
  },

  computed: {
    startMode() {
      return this.$route?.query?.mode || "";
    },
  },

  mounted() {
    if (this.startMode === "assistant") {
      this.mode = "assistant";
    }
  },

  methods: {
    go(next) {
      this.mode = next;
    },

    openReview(draft) {
      const normalized = { ...(draft || {}) };

      if (!normalized.acquisition_summary) {
        normalized.acquisition_summary = {
          source: "",
          date: "",
          entry_method: "assistant",
          total_cost: null,
          card_type: "",
        };
      }

      normalized.acquisition_summary.card_type = normalized.acquisition_summary.card_type || "";


      if (!Array.isArray(normalized.items)) normalized.items = [];

      this.draft = normalized;
      this.mode = "review";
    },

    reset() {
      this.draft = null;
      this.mode = "";
    },

    onConfirmed(result) {
      console.log("✅ acquisition confirmed:", result);
      this.reset();
      this.$router.push("/inventory");
    }

  },
};
</script>

<style scoped>
.card-head {
  margin-bottom: 12px;
}

.card-title {
  font-weight: 900;
  font-size: 16px;
  letter-spacing: 0.2px;
}

.card-hint {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.75;
  font-weight: 600;
}

.tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(210px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.tile {
  text-align: left;
  cursor: pointer;
  border-radius: 16px;
  border: 1px solid rgba(176, 196, 222, 0.9);
  background: #f6fbff;
  padding: 14px;
  transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.06);
}

.tile:hover {
  transform: translateY(-2px);
  box-shadow: 0px 10px 18px rgba(0, 0, 0, 0.08);
  background: #eef7ff;
}

.tile-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.tile-title {
  font-weight: 900;
  font-size: 15px;
}

.tile-sub {
  opacity: 0.8;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.25rem;
}

.badge {
  font-weight: 900;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 999px;
  color: white;
  background: #288671;
  box-shadow: 0px 6px 14px rgba(40, 134, 113, 0.18);
}

.badge.alt {
  background: #6b4fb3;
  box-shadow: 0px 6px 14px rgba(107, 79, 179, 0.18);
}

.badge.soon {
  background: #327ba8;
  box-shadow: 0px 6px 14px rgba(50, 123, 168, 0.18);
}

.tile.disabled {
  opacity: 0.92;
}

.mode-wrap {
  display: grid;
  gap: 12px;
}

.mode-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.mode-title {
  font-weight: 900;
  font-size: 16px;
}

.coming-text {
  font-size: 13px;
  font-weight: 650;
  opacity: 0.85;
  line-height: 1.35rem;
}

.cta-row {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
}

@media (max-width: 980px) {
  .tiles {
    grid-template-columns: 1fr;
  }
}
</style>
